import React, { useState } from 'react';
import './AuthForms.css';
import './UserLogin.css';
import { API_BASE_URL } from '../config';

interface UserLoginProps {
  onLoginSuccess?: (user: any) => void;
  onSwitchToPassword?: () => void;
}

const UserLogin: React.FC<UserLoginProps> = ({ onLoginSuccess, onSwitchToPassword }) => {
  const [step, setStep] = useState<'email' | 'code'>('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/request-code`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to send code');
      }

      const data = await response.json();
      setMessage(data.message);
      setStep('code');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send code');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCodeSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/verify-code`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, code }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Invalid code');
      }

      const user = await response.json();
      
      if (onLoginSuccess) {
        onLoginSuccess(user);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackToEmail = () => {
    setStep('email');
    setCode('');
    setError('');
    setMessage('');
  };

  if (step === 'code') {
    return (
      <div className="auth-container">
        <h2>Enter Login Code</h2>
        <p>We sent a 10-digit code to <strong>{email}</strong></p>
        {message && <div className="success-message">{message}</div>}

        <form onSubmit={handleCodeSubmit} className="auth-form">
          <div className="form-group">
            <label htmlFor="code">Login Code</label>
            <input
              type="text"
              id="code"
              value={code}
              onChange={(e) => {
                setCode(e.target.value.replace(/\D/g, '').slice(0, 10));
                setError('');
              }}
              required
              maxLength={10}
              placeholder="1234567890"
              className="code-input"
            />
            <small>Enter the 10-digit code from your email</small>
          </div>

          {error && <div className="error-message">{error}</div>}

          <button 
            type="submit" 
            disabled={isLoading || code.length !== 10}
            className="btn-primary"
          >
            {isLoading ? 'Verifying...' : 'Login'}
          </button>
        </form>

        <button onClick={handleBackToEmail} className="btn-secondary">
          Back to Email
        </button>
      </div>
    );
  }

  return (
    <div className="auth-container">
      <h2>Login</h2>
      <p>Enter your email to receive a login code</p>

      <form onSubmit={handleEmailSubmit} className="auth-form">
        <div className="form-group">
          <label htmlFor="email">Email</label>
          <input
            type="email"
            id="email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              setError('');
            }}
            required
            placeholder="your@email.com"
          />
        </div>

        {error && <div className="error-message">{error}</div>}

        <button
          type="submit"
          disabled={isLoading || !email}
          className="btn-primary"
        >
          {isLoading ? 'Sending Code...' : 'Send Login Code'}
        </button>
      </form>

      {onSwitchToPassword && (
        <div className="auth-toggle">
          <p>Prefer using a password?</p>
          <button
            onClick={onSwitchToPassword}
            className="auth-switch-btn"
          >
            Login with Password Instead
          </button>
        </div>
      )}
    </div>
  );
};

export default UserLogin;